import { LOGIN_PAGE } from '@/config/appRoutes';
import { isPublicUrl } from '@/config/routes';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to get information about the current route
 *
 * This hook provides utilities to check the current route state,
 * following the project's pattern of route-related utilities.
 *
 * @returns Object containing route information and utility functions
 */
export const useCurrentRoute = () => {
  const location = useLocation();

  const routeInfo = useMemo(() => {
    const currentPath = location.pathname;

    return {
      // Current location object
      location,

      // Current pathname
      pathname: currentPath,

      // Route state checks
      isLoginPage: currentPath === LOGIN_PAGE,
      isPublicRoute: isPublicUrl(currentPath),

      // Search params and state
      searchParams: new URLSearchParams(location.search),
      state: location.state,

      // Utility functions
      hasSearchParam: (key: string) => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.has(key);
      },

      getSearchParam: (key: string) => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get(key);
      },
    };
  }, [location]);

  return routeInfo;
};

/**
 * Simple hook to check if the current page is the login page
 *
 * @returns boolean - true if current page is the login page
 */
export const useIsLoginPage = () => {
  const { isLoginPage } = useCurrentRoute();
  return isLoginPage;
};
